import React, { useEffect, useState } from "react";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { EaSusSettings } from "@/components/energy-analytics/analytics/EaSusSettings";

const str = (v) => String(v).replace(".", ",");
const num = (t) => Number(String(t).replace(",", "."));

function FactorRow({ r, onSave, onReset }) {
  const [p, setP] = useState(str(r.power_kg_per_kwh));
  const [g, setG] = useState(str(r.gas_kg_per_smc));
  const [rm, setRm] = useState(str(r.residual_kg_per_kwh));
  useEffect(() => { setP(str(r.power_kg_per_kwh)); setG(str(r.gas_kg_per_smc)); setRm(str(r.residual_kg_per_kwh)); }, [r]);
  return (
    <tr className="border-t border-slate-100" data-testid={`admin-ef-row-${r.year}`}>
      <td className="px-3 py-2 font-semibold">{r.year}</td>
      <td className="px-3 py-2"><input value={p} onChange={(e) => setP(e.target.value)} className={`${inputCls} w-28 text-right`} data-testid={`admin-ef-power-${r.year}`} /></td>
      <td className="px-3 py-2"><input value={g} onChange={(e) => setG(e.target.value)} className={`${inputCls} w-28 text-right`} data-testid={`admin-ef-gas-${r.year}`} /></td>
      <td className="px-3 py-2"><input value={rm} onChange={(e) => setRm(e.target.value)} className={`${inputCls} w-28 text-right`} data-testid={`admin-ef-residual-${r.year}`} /></td>
      <td className="px-3 py-2 text-xs text-slate-500">{r.source === "Adybo" ? `Personalizzato (ISPRA ${str(r.ispra.power_kg_per_kwh)} · AIB ${str(r.ispra.residual_kg_per_kwh)})` : "ISPRA · residual mix AIB"}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <button onClick={() => onSave(r.year, num(p), num(g), num(rm))} className="text-slate-700 hover:text-slate-900 mr-3" title="Salva" data-testid={`admin-ef-save-${r.year}`}><Save size={16} /></button>
        {r.source === "Adybo" && <button onClick={() => onReset(r.year)} className="text-slate-400 hover:text-red-600" title="Ripristina ISPRA" data-testid={`admin-ef-reset-${r.year}`}><RotateCcw size={16} /></button>}
      </td>
    </tr>
  );
}

function GlobalFactors() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/ea/sustainability/global-factors").then((r) => setRows(r.data.years)); }, []);
  const save = async (year, p, g, rm) => {
    if (!(p > 0) || !(g > 0) || !(rm > 0)) return toast.error("Inserisci fattori numerici maggiori di zero");
    try { const r = await api.put("/ea/sustainability/global-factors", { year, power_kg_per_kwh: p, gas_kg_per_smc: g, residual_kg_per_kwh: rm }); setRows(r.data.years); toast.success(`Fattori ${year} salvati`); }
    catch { toast.error("Valori non validi"); }
  };
  const reset = async (year) => { const r = await api.delete(`/ea/sustainability/global-factors/${year}`); setRows(r.data.years); toast.success(`Ripristinati i valori ISPRA ${year}`); };
  return (
    <div className="card-elevated overflow-x-auto" data-testid="admin-emission-factors">
      <div className="p-5 pb-2">
        <div className="font-display font-semibold text-slate-900">Fattori emissivi per anno (tutti i clienti)</div>
        <div className="text-xs text-slate-500">Valori di partenza ISPRA (mix consumi elettrici nazionale e gas naturale) e residual mix AIB per lo Scope 2 market-based (GHG Protocol): il residual mix è l'intensità emissiva dell'energia non coperta da Garanzie di Origine. Ogni cliente può impostare valori propri nella sezione Sostenibilità, che prevalgono su questi.</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Anno", "kg CO₂/kWh (location)", "kg CO₂/Smc", "Residual mix kg CO₂/kWh", "Fonte", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <FactorRow key={r.year} r={r} onSave={save} onReset={reset} />)}</tbody>
      </table>
    </div>
  );
}

export function AdminSustainability({ companyId = "" }) {
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState(companyId);
  useEffect(() => { setCid(companyId); }, [companyId]);
  const [nav, setNav] = useState(null);
  useEffect(() => { api.get("/ea/companies").then((r) => setCompanies(r.data.filter((c) => c.has_data))); }, []);
  useEffect(() => {
    setNav(null);
    if (cid) api.get("/ea/navigation", { params: { company_id: cid } }).then((r) => setNav(r.data));
  }, [cid]);
  return (
    <div className="space-y-6" data-testid="admin-sustainability">
      <h2 className="font-display text-xl font-bold text-slate-900">KPI & Sostenibilità</h2>
      <GlobalFactors />
      <div className="card-elevated p-5 space-y-4">
        <div>
          <div className="font-display font-semibold text-slate-900">Quota rinnovabile e fattori per cliente</div>
          <div className="text-xs text-slate-500">Inserisci per cliente e anno autoproduzione, PPA on-site/off-site, biometano (per punto e mese), Garanzie di Origine e anno base del Report ESG. Anche il cliente può modificarli.</div>
        </div>
        <select value={cid} onChange={(e) => setCid(e.target.value)} className={`${inputCls} max-w-sm`} data-testid="admin-sus-company">
          <option value="">Seleziona un cliente…</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {nav && <EaSusSettings companyId={cid} years={nav.years} year={nav.year} points={nav.points} />}
      </div>
    </div>
  );
}
